import { useConsultationStore } from '@/features/chat/store/consultationStore';
import { create } from 'zustand';
import { projectApi } from '../lib/api/projects';
import type { ProjectApiRecord, ProjectCategoryApiRecord } from '../lib/api/projects';
import { API_BASE_URL } from '../lib/api/client';
import { authFetch } from '@/lib/auth/session';
import { getAuthScopeRevision, AUTH_SCOPED_STATE_RESET_EVENT } from '@/lib/auth-scoped-state-reset';

/** 简单的认证 fetch */
async function authJsonFetch(path: string, init: RequestInit = {}) {
  const res = await authFetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers as Record<string, string> || {}) },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// 调试开关
const DEBUG_ENABLED = process.env.NODE_ENV === 'development';
const debugError = (...args: unknown[]) => { if (DEBUG_ENABLED) console.error(...args); };
const PROJECT_ERROR = {
  LOAD_FAILED: 'project_load_failed',
  DELETE_FAILED: 'project_delete_failed',
  ARCHIVE_FAILED: 'project_archive_failed',
} as const;

export interface ProjectRecord {
  id: string;
  name: string;
  categoryId?: string | null;
  duration?: number;
  thumbnailUrl?: string;
  thumbnailAssetId?: string;  // 用于动态加载封面的 asset ID
  updatedAt: string;
  createdAt?: string;  // 创建时间
  status?: 'processing' | 'completed' | 'archived';  // 项目状态
  nodeCount?: number;  // 画布节点数
  role?: string;       // 协作角色（仅协作项目）
}

export interface ProjectCategoryRecord {
  id: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface ProjectStoreState {
  projects: ProjectRecord[];
  projectCategories: ProjectCategoryRecord[];
  collaboratedProjects: ProjectRecord[];
  loading: boolean;
  categoriesLoading: boolean;
  error: string | null;
  projectsFetchedAt: number;
  categoriesFetchedAt: number;
  collaboratedFetchedAt: number;
  collaboratedLoading: boolean;
  invalidateProjects: () => void;
  hasFetched: boolean; // 标记是否已获取过
  
  // Actions
  fetchProjects: (force?: boolean) => Promise<void>;
  fetchProjectCategories: (force?: boolean) => Promise<void>;
  fetchCollaboratedProjects: () => Promise<void>;
  addProject: (project: ProjectRecord) => void;
  updateProject: (id: string, updates: Partial<ProjectRecord>) => void;
  forgetProjectAccess: (id: string) => void;
  resetAuthScopedState: () => void;
  createCategory: (name: string) => Promise<ProjectCategoryRecord | null>;
  updateCategory: (id: string, updates: { name?: string; sortOrder?: number }) => Promise<boolean>;
  deleteCategory: (id: string) => Promise<boolean>;
  moveProjectToCategory: (projectId: string, categoryId: string | null) => Promise<boolean>;
  removeProject: (id: string) => Promise<void>;
  removeProjects: (ids: string[]) => Promise<{ success: number; failed: number }>;
  archiveProject: (id: string) => Promise<void>;
  getProject: (id: string) => ProjectRecord | undefined;
}

interface BatchDeleteProjectResult {
  id: string;
  success: boolean;
}

interface BatchDeleteProjectsResponse {
  results?: BatchDeleteProjectResult[];
  success_count: number;
  fail_count: number;
}

function mapCategoryRecord(item: ProjectCategoryApiRecord): ProjectCategoryRecord {
  return {
    id: item.id,
    name: item.name,
    sortOrder: item.sort_order,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

export function mapProjectApiRecord(item: ProjectApiRecord): ProjectRecord {
  return {
    id: item.id,
    name: item.name,
    categoryId: item.category_id ?? null,
    duration: item.duration,
    thumbnailUrl: item.thumbnail_url ?? undefined,
    thumbnailAssetId: item.thumbnail_asset_id ?? undefined,
    updatedAt: item.updated_at,
    createdAt: item.created_at,
    status: item.status as ProjectRecord['status'],
    nodeCount: item.node_count ?? 0,
    role: item.role ?? 'owner',
  };
}

export const useProjectStore = create<ProjectStoreState>()((set, get) => ({
  projects: [],
  projectCategories: [],
  collaboratedProjects: [],
  loading: false,
  categoriesLoading: false,
  error: null,
  hasFetched: false,
  projectsFetchedAt: 0, categoriesFetchedAt: 0, collaboratedFetchedAt: 0, collaboratedLoading: false,
  
  fetchProjects: async (force = false) => {
    // 防止重复请求（但允许强制刷新）
    if (get().loading) return;
    if (!force && get().hasFetched && Date.now() - get().projectsFetchedAt < 30_000) return;
    const scope = getAuthScopeRevision();
    
    set({ loading: true, error: null });
    try {
      const response = await projectApi.getProjects({ limit: 100 });
      if (response.error || !response.data) {
        throw new Error(PROJECT_ERROR.LOAD_FAILED);
      }
      if (scope !== getAuthScopeRevision()) return;
      const projects = response.data.items.map(mapProjectApiRecord);
      set({ projects, loading: false, hasFetched: true, projectsFetchedAt: Date.now() });
    } catch (error) {
      if (scope !== getAuthScopeRevision()) return;
      debugError('Failed to load project list:', error);
      set({ 
        error: PROJECT_ERROR.LOAD_FAILED,
        loading: false 
      });
    }
  },

  invalidateProjects: () => set({ projectsFetchedAt: 0 }),

  fetchProjectCategories: async (force = false) => {
    if (get().categoriesLoading) return;
    if (!force && get().categoriesFetchedAt && Date.now() - get().categoriesFetchedAt < 30_000) return;
    const scope = getAuthScopeRevision();

    set({ categoriesLoading: true });
    try {
      const response = await projectApi.getProjectCategories();
      if (response.error || !response.data) {
        throw new Error(PROJECT_ERROR.LOAD_FAILED);
      }
      if (scope !== getAuthScopeRevision()) return;
      set({
        categoriesFetchedAt: Date.now(),
        projectCategories: response.data.items.map(mapCategoryRecord),
        categoriesLoading: false,
      });
    } catch (error) {
      if (scope !== getAuthScopeRevision()) return;
      debugError('Failed to load project categories:', error);
      set({ categoriesLoading: false });
    }
  },

  fetchCollaboratedProjects: async () => {
    if (get().collaboratedLoading || (get().collaboratedFetchedAt && Date.now() - get().collaboratedFetchedAt < 30_000)) return;
    const scope = getAuthScopeRevision();
    set({ collaboratedLoading: true });
    try {
      const response = await projectApi.getCollaboratedProjects();
      if (response.error || !response.data) return;
      const projects: ProjectRecord[] = response.data.items.map((item) => ({
        id: item.id,
        name: item.name,
        categoryId: item.category_id ?? null,
        thumbnailUrl: item.thumbnail_url ?? undefined,
        updatedAt: item.updated_at,
        nodeCount: item.node_count ?? 0,
        role: item.role,
      }));
      if (scope !== getAuthScopeRevision()) return;
      set({ collaboratedProjects: projects, collaboratedFetchedAt: Date.now() });
    } catch {
      // 静默失败 — 协作列表获取失败不影响主列表
    } finally {
      if (scope === getAuthScopeRevision()) set({ collaboratedLoading: false });
    }
  },
  
  addProject: (project) => {
    set((state) => ({
      projects: [project, ...state.projects.filter((item) => item.id !== project.id)],
    }));
  },
  
  updateProject: (id, updates) => {
    set((state) => {
      const updated = state.projects.map((p) =>
        p.id === id 
          ? { ...p, ...updates, updatedAt: new Date().toISOString() } 
          : p
      );
      // 按 updatedAt 降序重排，保证最近活跃的项目在最前
      updated.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      return { projects: updated };
    });
  },

  forgetProjectAccess: (id) => {
    set((state) => ({
      projects: state.projects.filter((project) => project.id !== id),
      collaboratedProjects: state.collaboratedProjects.filter((project) => project.id !== id),
    }));
  },

  resetAuthScopedState: () => {
    projectApi.clearProjectMetaCache();
    useConsultationStore.getState().resetAll();
    set({
      projects: [],
      projectCategories: [],
      collaboratedProjects: [],
      loading: false,
      categoriesLoading: false,
      error: null,
      hasFetched: false,
      projectsFetchedAt: 0, categoriesFetchedAt: 0, collaboratedFetchedAt: 0, collaboratedLoading: false,
    });
  },

  createCategory: async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return null;

    try {
      const response = await projectApi.createProjectCategory(trimmed);
      if (response.error || !response.data) {
        throw new Error(PROJECT_ERROR.LOAD_FAILED);
      }
      const category = mapCategoryRecord(response.data);
      set((state) => ({
        projectCategories: [...state.projectCategories, category].sort((a, b) => a.sortOrder - b.sortOrder),
      }));
      return category;
    } catch (error) {
      debugError('Failed to create project category:', error);
      return null;
    }
  },

  updateCategory: async (id, updates) => {
    const payload: { name?: string; sort_order?: number } = {};
    if (updates.name !== undefined) {
      const trimmed = updates.name.trim();
      if (!trimmed) return false;
      payload.name = trimmed;
    }
    if (updates.sortOrder !== undefined) {
      payload.sort_order = updates.sortOrder;
    }

    try {
      const response = await projectApi.updateProjectCategory(id, payload);
      if (response.error || !response.data) {
        throw new Error(PROJECT_ERROR.LOAD_FAILED);
      }
      const category = mapCategoryRecord(response.data);
      set((state) => ({
        projectCategories: state.projectCategories
          .map((item) => (item.id === id ? category : item))
          .sort((a, b) => a.sortOrder - b.sortOrder),
      }));
      return true;
    } catch (error) {
      debugError('Failed to update project category:', error);
      return false;
    }
  },

  deleteCategory: async (id) => {
    const prevCategories = get().projectCategories;
    const prevProjects = get().projects;
    set((state) => ({
      projectCategories: state.projectCategories.filter((category) => category.id !== id),
      projects: state.projects.map((project) => (
        project.categoryId === id ? { ...project, categoryId: null } : project
      )),
    }));

    try {
      const response = await projectApi.deleteProjectCategory(id);
      if (response.error) {
        throw new Error(PROJECT_ERROR.DELETE_FAILED);
      }
      return true;
    } catch (error) {
      debugError('Failed to delete project category:', error);
      set({ projectCategories: prevCategories, projects: prevProjects });
      return false;
    }
  },

  moveProjectToCategory: async (projectId, categoryId) => {
    const prevProjects = get().projects;
    set((state) => ({
      projects: state.projects.map((project) => (
        project.id === projectId ? { ...project, categoryId } : project
      )),
    }));

    try {
      const response = await projectApi.updateProject(projectId, { category_id: categoryId });
      if (response.error || !response.data) {
        throw new Error(PROJECT_ERROR.ARCHIVE_FAILED);
      }
      const updatedProject = response.data;
      const latest = get().projects;
      set({
        projects: latest.map((project) => (
          project.id === projectId
            ? { ...project, categoryId: updatedProject.category_id ?? null, updatedAt: updatedProject.updated_at ?? project.updatedAt }
            : project
        )),
      });
      return true;
    } catch (error) {
      debugError('Failed to move project to category:', error);
      set({ projects: prevProjects });
      return false;
    }
  },
  
  removeProject: async (id) => {
    // 乐观删除：立即从列表移除，API 失败再恢复
    const prev = get().projects;
    set((state) => ({
      projects: state.projects.filter((p) => p.id !== id),
    }));
    try {
      const response = await projectApi.deleteProject(id);
      if (response.error) {
        throw new Error(PROJECT_ERROR.DELETE_FAILED);
      }
      const { clearPendingThreadRecoveryForProject } = await import('@/features/chat/store/chatStore');
      clearPendingThreadRecoveryForProject(id);
    } catch (error) {
      debugError('Failed to delete project:', error);
      set({ projects: prev });
      throw error;
    }
  },
  
  removeProjects: async (ids) => {
    try {
      const result = await authJsonFetch('/projects/batch-delete', {
        method: 'POST',
        body: JSON.stringify({ project_ids: ids }),
      }) as BatchDeleteProjectsResponse;
      // 从列表中移除成功删除的项目
      const successIds = (result.results || [])
        .filter((r) => r.success)
        .map((r) => r.id);
      
      set((state) => ({
        projects: state.projects.filter((p) => !successIds.includes(p.id)),
      }));
      const { clearPendingThreadRecoveryForProject } = await import('@/features/chat/store/chatStore');
      successIds.forEach((projectId) => clearPendingThreadRecoveryForProject(projectId));
      
      return { success: result.success_count, failed: result.fail_count };
    } catch (error) {
      debugError('Failed to delete projects:', error);
      throw error;
    }
  },
  
  archiveProject: async (id) => {
    try {
      const response = await projectApi.updateProject(id, { status: 'archived' });
      if (response.error) {
        throw new Error(PROJECT_ERROR.ARCHIVE_FAILED);
      }
      get().updateProject(id, { status: 'archived' });
    } catch (error) {
      debugError('Failed to archive project:', error);
      throw error;
    }
  },
  
  getProject: (id) => {
    return get().projects.find((p) => p.id === id);
  },
}));

interface ProjectStoreAuthResetRegistry {
  reset: () => void;
  listener?: () => void;
}

const projectStoreAuthResetRegistry = globalThis as typeof globalThis & {
  __dobiProjectStoreAuthResetRegistry?: ProjectStoreAuthResetRegistry;
};

if (typeof window !== 'undefined') {
  const registry = projectStoreAuthResetRegistry.__dobiProjectStoreAuthResetRegistry ?? {
    reset: () => undefined,
  };
  registry.reset = () => useProjectStore.getState().resetAuthScopedState();
  if (!registry.listener) {
    registry.listener = () => registry.reset();
    window.addEventListener(AUTH_SCOPED_STATE_RESET_EVENT, registry.listener);
  }
  projectStoreAuthResetRegistry.__dobiProjectStoreAuthResetRegistry = registry;
}
